import EditIcon from '@/components/icons/EditIcon';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type {
  OfferTemplate,
  OfferTemplateContentInput,
  OfferTemplateInput,
} from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { Loader2, Play, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import OfferForm, { type OfferFormValues } from '../../create-webinar/live/offer/OfferForm';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { toast } from 'react-toastify';
import Loader from '@/components/ui/Loader';
import { useTranslation } from 'react-i18next';
import type { AppError } from '@/features/auth/types';
import { mapErrorMessage, mapHttpError } from '@/lib/mapApiError';
import type { LiveSessionResourceStateDto, TriggerEventInput } from '@/features/live/types';
import {
  deleteOfferTemplate,
  listOfferTemplates,
  updateOfferTemplate,
  uploadOfferTemplateContentImage,
  upsertOfferTemplateContent,
} from '@/features/webinar/webinarThunks';
import { fetchOfferResults } from '@/features/live/liveThunks';
import type { RootState } from '@/app/store';
import OfferCard from '@/components/common/asset/OfferCard';
import { toOfferConversionInput } from '@/lib/offerEmbed';

const getColorValueById = (
  colorId: string | undefined,
  customColor: string | undefined,
  defaultColor: string,
  visualIdentity: WorkspaceVisualIdentity | null | undefined,
): string => {
  if (!colorId || colorId === 'custom') {
    return customColor || defaultColor;
  }

  if (/^#[0-9A-Fa-f]{6}$/.test(colorId)) {
    return colorId;
  }

  if (visualIdentity) {
    if (colorId === 'accent') {
      return visualIdentity.accentColor || defaultColor;
    }
    if (colorId === 'background') {
      return visualIdentity.backgroundColor || defaultColor;
    }
    if (colorId === 'contrast') {
      return visualIdentity.contrastColor || defaultColor;
    }
    if (colorId === 'secondary-background') {
      return visualIdentity.secondaryBackgroundColor || defaultColor;
    }
    if (colorId === 'gradient1-color1') {
      return 'gradient1';
    }
    if (colorId === 'gradient2-color1') {
      return 'gradient2';
    }
  }

  if (colorId === 'black') {
    return '#000000';
  }
  if (colorId === 'white') {
    return '#FFFFFF';
  }
  if (colorId === 'no-border') {
    return 'transparent';
  }

  return defaultColor;
};

const mapOfferContentToInput = (
  data: OfferFormValues,
  type: 'A' | 'B',
  visualIdentity: WorkspaceVisualIdentity | null | undefined,
): OfferTemplateContentInput => {
  const showIcon = type === 'A' ? data.showIconA : (data.showIconB ?? false);
  const title = type === 'A' ? data.titleA : data.titleB || '';
  const subTitle = type === 'A' ? data.subTitleA : data.subTitleB || null;
  const description = type === 'A' ? data.descriptionA : data.descriptionB || null;
  const buttonLabel = type === 'A' ? data.buttonLabelA : data.buttonLabelB || '';
  const conversion = toOfferConversionInput(
    type === 'A' ? data.conversionModeA : data.conversionModeB,
    type === 'A' ? data.buttonLinkA : data.buttonLinkB,
    type === 'A' ? data.embedCodeA : data.embedCodeB,
    type === 'A' ? data.stripePricesA : data.stripePricesB,
  );
  const backgroundColor = getColorValueById(
    type === 'A' ? data.backgroundColorA : data.backgroundColorB,
    type === 'A' ? data.customBackgroundColorA : data.customBackgroundColorB,
    '#000000',
    visualIdentity,
  );
  const borderColorId = type === 'A' ? data.borderColorA : data.borderColorB;
  const borderColor =
    borderColorId === 'no-border'
      ? null
      : getColorValueById(
          borderColorId,
          type === 'A' ? data.customBorderColorA : data.customBorderColorB,
          '#000000',
          visualIdentity,
        );
  const titleColor = type === 'A' ? data.titleColorA : data.titleColorB || '#FFFFFF';
  const textColor = type === 'A' ? data.textColorA : data.textColorB || '#FFFFFF';
  const buttonColor = getColorValueById(
    type === 'A' ? data.buttonColorA : data.buttonColorB,
    type === 'A' ? data.customButtonColorA : data.customButtonColorB,
    '#000000',
    visualIdentity,
  );
  const buttonTextColor = type === 'A' ? data.buttonTextColorA : data.buttonTextColorB || '#000000';
  const icon = showIcon ? (type === 'A' ? data.iconA : data.iconB || null) : null;

  return {
    title,
    subTitle,
    description,
    buttonLabel,
    ...conversion,
    backgroundColor,
    borderColor,
    titleColor,
    textColor,
    buttonColor,
    buttonTextColor,
    icon,
    iconColor: '#000000',
    iconBackgroundColor: '#000000',
  };
};

export default function OfferList({
  offers,
  // loading,
  visualIdentity,
  workspaceId,
  webinarId,
  sessionId,
  adminResourceStates,
  handleTriggerEvent,
  liveParticipantCountSync,
  isModeratorLive = false,
}: {
  offers: OfferTemplate[];
  visualIdentity: WorkspaceVisualIdentity;
  workspaceId: number;
  webinarId: number;
  sessionId?: number;
  adminResourceStates: LiveSessionResourceStateDto[];
  handleTriggerEvent?: (event: TriggerEventInput) => void;
  liveParticipantCountSync?: number;
  isModeratorLive?: boolean;
}) {
  const dispatch = useAppDispatch();
  const offerResultsByOfferId = useAppSelector(
    (s: RootState) => s.live.offerResultsByOfferId ?? {},
  );
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('createWebinar');
  const [selectedOffer, setSelectedOffer] = useState<OfferTemplate | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loadingOfferId, setLoadingOfferId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEditOffer = (offer: any) => {
    setSelectedOffer(offer);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedOffer(null);
  };

  const handleOfferSubmit = async (data: OfferFormValues): Promise<number | null> => {
    if (!selectedOffer?.id) {
      console.error('No offer selected');
      return null;
    }

    setIsSubmitting(true);
    try {
      const templateId = selectedOffer.id;
      const existingOffer = selectedOffer;

      const hasTemplateFieldsChanged =
        existingOffer.privateTitle !== data.privateTitle ||
        existingOffer.abTesting !== (data.abTesting ?? false) ||
        existingOffer.splitPercentA !== (data.abTesting ? (data.splitPercentA ?? 50) : null);

      if (hasTemplateFieldsChanged) {
        const offerTemplateInput: OfferTemplateInput = {
          privateTitle: data.privateTitle,
          abTesting: data.abTesting ?? false,
          splitPercentA: data.abTesting ? (data.splitPercentA ?? 50) : null,
        };

        await dispatch(
          updateOfferTemplate({
            workspaceId,
            webinarId,
            offerId: templateId,
            input: offerTemplateInput,
          }),
        ).unwrap();
      }

      const contentA: OfferTemplateContentInput = mapOfferContentToInput(data, 'A', visualIdentity);
      await dispatch(
        upsertOfferTemplateContent({
          workspaceId,
          webinarId,
          templateId,
          contentType: 'A',
          input: contentA,
        }),
      ).unwrap();

      if (data.showImageA && data.imgA instanceof File) {
        await dispatch(
          uploadOfferTemplateContentImage({
            workspaceId,
            webinarId,
            templateId,
            contentType: 'A',
            file: data.imgA,
          }),
        ).unwrap();
      }

      if (data.abTesting) {
        const contentB: OfferTemplateContentInput = mapOfferContentToInput(
          data,
          'B',
          visualIdentity,
        );
        await dispatch(
          upsertOfferTemplateContent({
            workspaceId,
            webinarId,
            templateId,
            contentType: 'B',
            input: contentB,
          }),
        ).unwrap();

        if (data.showImageB && data.imgB instanceof File) {
          await dispatch(
            uploadOfferTemplateContentImage({
              workspaceId,
              webinarId,
              templateId,
              contentType: 'B',
              file: data.imgB,
            }),
          ).unwrap();
        }
      }

      await dispatch(listOfferTemplates({ workspaceId, webinarId })).unwrap();

      if (handleTriggerEvent) {
        await handleTriggerEvent({
          eventType: 'RESOURCE_UPDATE',
          resourceType: 'OFFER_TEMPLATE',
          resourceId: templateId,
        });
      }

      toast.success(t('offersForm.offerUpdatedSuccess'));
      return selectedOffer.id;
    } catch (error) {
      console.error('Error updating offer:', error);

      const appError = error as AppError | undefined;

      if (appError?.message) {
        const mainMessage = tCommon(
          mapErrorMessage(appError.message, appError.code, appError.status),
        );
        toast.error(mainMessage);
      } else if (error instanceof Error && error.message) {
        toast.error(tCommon('errors.generic'));
      } else {
        toast.error(tCommon('errors.generic'));
      }

      if (appError?.details && Array.isArray(appError.details)) {
        appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
          if (detail.message) {
            const mappedMessage = mapHttpError(detail.message);
            const translatedMessage = tCommon(mappedMessage, detail.message);
            toast.error(translatedMessage);
          }
        });
      }
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteOffer = async () => {
    if (!selectedOffer?.id) {
      console.error('No offer selected');
      return;
    }

    const confirmed = window.confirm(t('offersForm.deleteConfirm'));

    if (!confirmed) {
      return;
    }

    try {
      await dispatch(
        deleteOfferTemplate({
          workspaceId,
          webinarId,
          offerId: selectedOffer.id,
        }),
      ).unwrap();

      await dispatch(listOfferTemplates({ workspaceId, webinarId })).unwrap();

      setSelectedOffer(null);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error deleting offer:', error);
    }
  };

  const handleSaveFromModal = () => {
    if ((window as any).offerFormSave) {
      (window as any).offerFormSave();
    }
  };

  const handleToggleOfferLive = async (offer: OfferTemplate) => {
    if (!handleTriggerEvent) return;

    const isOfferLive = adminResourceStates.some(
      (state) =>
        state.resourceType === 'OFFER_TEMPLATE' && state.resourceId === offer.id && state.live,
    );

    setLoadingOfferId(offer.id);
    try {
      if (isOfferLive) {
        await handleTriggerEvent({
          eventType: 'RESOURCE_HIDE',
          resourceType: 'OFFER_TEMPLATE',
          resourceId: offer.id,
          payloadJson: JSON.stringify({
            timeNow: new Date().getTime(),
          }),
        });
        if (workspaceId && webinarId) {
          setTimeout(() => {
            void dispatch(
              fetchOfferResults({
                workspaceId,
                webinarId,
                offerId: offer.id,
                sessionId,
              }),
            ).catch((error) => {
              console.error('Error fetching offer results:', error);
            });
          }, 0);
        }
      } else {
        await handleTriggerEvent({
          eventType: 'RESOURCE_SHOW',
          resourceType: 'OFFER_TEMPLATE',
          resourceId: offer.id,
          payloadJson: JSON.stringify({
            timeNow: new Date().getTime(),
          }),
        });
        if (workspaceId && webinarId && sessionId != null) {
          void dispatch(
            fetchOfferResults({
              workspaceId,
              webinarId,
              offerId: offer.id,
              sessionId,
            }),
          ).catch((error) => {
            console.error('Error fetching offer results:', error);
          });
        }
      }
    } catch (error) {
      console.error('Error toggling offer live:', error);
    } finally {
      setLoadingOfferId(null);
    }
  };

  return (
    <div
      className="flex flex-col gap-4"
      style={{ color: visualIdentity?.theme === 'DARK' ? '#FFFFFF' : '#000000' }}
    >
      {offers?.map((offer) => {
        const isOfferLive = adminResourceStates.some(
          (state) =>
            state.resourceType === 'OFFER_TEMPLATE' && state.resourceId === offer.id && state.live,
        );

        return (
          <div key={offer.id} className="w-full">
            <p className="text-sm font-medium pb-4">{offer.privateTitle}</p>
            <div className=" flex  gap-[10px] ">
              {/* offer */}
              <OfferCard
                offer={offer}
                visualIdentity={visualIdentity}
                attendeeView={false}
                isAdmin={!isModeratorLive}
                isModerator={isModeratorLive}
                offerLive={isOfferLive}
                offerResults={offerResultsByOfferId[offer.id] ?? null}
                webinarId={webinarId}
                liveParticipantCountSync={liveParticipantCountSync}
              />
              {/* settings */}
              <div className="flex flex-col gap-4 ">
                <button
                  className="rounded-lg p-3 cursor-pointer"
                  onClick={() => handleToggleOfferLive(offer)}
                  disabled={loadingOfferId === offer.id}
                  style={{
                    backgroundColor: isOfferLive
                      ? '#FFD753'
                      : visualIdentity?.contrastColor || '#2356CC',
                  }}
                >
                  {loadingOfferId === offer.id ? (
                    <Loader2
                      size={18}
                      color={isOfferLive ? '#000000' : '#FFFFFF'}
                      className="animate-spin"
                    />
                  ) : isOfferLive ? (
                    <X size={18} color="#000000" />
                  ) : (
                    <Play size={18} color="#FFFFFF" />
                  )}
                </button>
                <button
                  className="rounded-lg p-3 cursor-pointer disabled:bg-medium disabled:cursor-not-allowed"
                  disabled={isOfferLive || loadingOfferId === offer.id}
                  onClick={() => handleEditOffer(offer)}
                  style={{
                    backgroundColor: visualIdentity?.contrastColor || '#2356CC',
                  }}
                >
                  <EditIcon
                    size={18}
                    color={isOfferLive || loadingOfferId === offer.id ? '#C5C5CA' : '#FFFFFF'}
                  />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Modal d'édition du quiz */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-medium" hideCloseButton>
          <DialogHeader className="hidden">
            <DialogTitle>{t('offersForm.editModalTitle')}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between">
            <p className="text-text-secondary">{t('offersForm.tools')}</p>
            <div className="flex items-center gap-6">
              <button
                type="button"
                onClick={handleDeleteOffer}
                className="bg-white rounded-lg py-3 px-2 border border-input cursor-pointer"
              >
                <Trash2 className="size-6" />
              </button>

              <button type="button" onClick={handleCloseModal} className=" cursor-pointer">
                {t('offersForm.cancel')}
              </button>

              <Button onClick={handleSaveFromModal} disabled={isSubmitting}>
                {isSubmitting ? <Loader /> : t('offersForm.saveButton')}
              </Button>
            </div>
          </div>
          {selectedOffer && (
            <OfferForm
              offerIndex={0}
              accordionValue={`offer-${selectedOffer.id}`}
              onRemove={() => {}}
              canRemove={false}
              onSubmit={handleOfferSubmit}
              existingOffer={selectedOffer}
              isModal={true}
              onSave={handleSaveFromModal}
              onDelete={handleDeleteOffer}
              workspaceId={workspaceId}
              webinarId={webinarId}
              templateId={selectedOffer.id}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
